import { DoorOperationType } from "building-types/doors/doorOperationTypes";
import type { GarageLights } from "building-types/electrical/electricalLayoutTypes";
import { ElectricalLayoutType } from "building-types/electrical/electricalLayoutTypesEnum";
import { LoadTypeSlot } from "building-types/electrical/electricalSlots";
import {
  ControllerSelectionType,
  ControllerSwitchType,
  ControllerType,
} from "building-types/electrical/electricalTypesEnum";
import type { GeneratedElectricalGroup, Load } from "building-types/electrical/generatedElectricalTypes";
import { WallType } from "building-types/walls/wallEnums";
import { ensureDefined } from "common/utils/assertOfType";
import { getLoadCatalog } from "doc/getLoadCatalog";
import { makeBuiltInLabelTable } from "doc/utils/labelTools";
import { aabb2ByPoints, aabb2Center } from "geom/aabb2";
import { line2PerpDistance } from "geom/line2";
import {
  polyLine2Area,
  polyLine2ByTwoPoints,
  polyLine2Centroid,
  polyLine2Edge,
  polyLine2EdgeEval,
  polyLine2EdgeEvalOffset,
  polyLine2EdgeNormal,
  polyLine2End,
  polyLine2Start,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { vec2Dist, vec2Scale } from "geom/vec2";
import { commitControllerSelection } from "../controllerSelection/controllerSelectionCommit";
import type { ElectricalLayoutFunc, ElectricalLayoutOpts } from "../electricalLayoutTypes";
import { loadTypeFromSlots } from "../loadTypeFromSlots";

interface LoadLocation {
  readonly x: number;
  readonly y: number;
  readonly nx: number;
  readonly ny: number;
}

export function garageLights({
  addOpener = false,
  controllerPlacementType = [{ type: ControllerSelectionType.PRIMARY_ENTRANCE }],
  loadSlot = LoadTypeSlot.FLOURESCENT_CEILING_LIGHT,
  loadType,
  groupLightsInRoomSet,
}: GarageLights): ElectricalLayoutFunc {
  return async (opts: ElectricalLayoutOpts) => {
    const {
      room: sourceRoom,
      wallIndex,
      controllerSelectionFunc,
      elecSlots,
      roomSetTable,
      roomsInRoomSetRuleCanBeAppliedTo,
      ruleAppliedToRoomTable,
      electricalItemCatalogSettings,
    } = opts;

    let largestRmId: string | undefined;
    let largestRmArea = 0;

    if (groupLightsInRoomSet) {
      for (const rmId in roomsInRoomSetRuleCanBeAppliedTo) {
        const roomSetRoom = roomSetTable[rmId];
        const area = polyLine2Area(roomSetRoom.outline);
        if (area > largestRmArea) {
          largestRmArea = area;
          largestRmId = rmId;
        }
      }
      if (largestRmId !== sourceRoom.id) {
        return [];
      }
    }

    const resolvedLoadType = loadType ?? loadTypeFromSlots(loadSlot, elecSlots);
    const loadCatalog = getLoadCatalog(electricalItemCatalogSettings);
    const loadDescriptor = loadCatalog[resolvedLoadType];

    const lightLocs: LoadLocation[] = [];
    const openerLocs: LoadLocation[] = [];

    for (const rmId in roomsInRoomSetRuleCanBeAppliedTo) {
      if (!groupLightsInRoomSet && rmId !== sourceRoom.id) {
        continue;
      }

      ruleAppliedToRoomTable[rmId] = true;
      const room = roomSetTable[rmId];

      const { outline } = room;
      const [cX, cY] = ensureDefined(polyLine2Centroid(outline));
      const walls = wallIndex.wallsInRoom(room);
      let baseLine = polyLine2Edge(outline, 0);

      for (const wall of walls) {
        if (wall.wallType !== WallType.SOLID) {
          continue;
        }

        for (const door of wall.doorPlacements) {
          const { id: doorId } = door;
          if (door.descriptor.operationType === DoorOperationType.GARAGE_SINGLE) {
            if (door.descriptor.isLegacyGarageDoorType) {
              continue;
            }
            const edgeIndex = wallIndex.planarGraph.halfEdgeIndexToPolygonEdgeIndex(door.halfEdgeIndex);
            const wallStrip = wallIndex.wallStripFromRoomEdgeIndex(room, edgeIndex);
            if (!wallStrip) {
              continue;
            }
            baseLine = wallStrip.baseline(true);
            const [sX, sY] = polyLine2Start(baseLine);
            const [eX, eY] = polyLine2End(baseLine);
            const params = wallStrip.doorGap(doorId, false);
            if (!params) {
              continue;
            }
            const { start, end, center } = params;
            addToLoadLocations(baseLine, center, sX, sY, eX, eY, cX, cY, lightLocs);

            const [dsX, dsY] = polyLine2EdgeEval(baseLine, 0, start, false);
            const [deX, deY] = polyLine2EdgeEval(baseLine, 0, end, false);
            const doorEdge = polyLine2ByTwoPoints(dsX, dsY, deX, deY);

            const [fX, fY] = polyLine2EdgeEvalOffset(doorEdge, 0, -60, 0.5, true);
            const [nx, ny] = polyLine2EdgeNormal(doorEdge, 0);
            openerLocs.push({ x: fX, y: fY, nx, ny });
          } else if (door.descriptor.operationType === DoorOperationType.GARAGE_DOUBLE) {
            if (door.descriptor.isLegacyGarageDoorType) {
              continue;
            }
            const edgeIndex = wallIndex.planarGraph.halfEdgeIndexToPolygonEdgeIndex(door.halfEdgeIndex);
            const wallStrip = wallIndex.wallStripFromRoomEdgeIndex(room, edgeIndex);
            if (!wallStrip) {
              continue;
            }
            baseLine = wallStrip.baseline(true);
            const params = wallStrip.doorGap(doorId, false);
            const [sX, sY] = polyLine2Start(baseLine);
            const [eX, eY] = polyLine2End(baseLine);
            if (!params) {
              continue;
            }
            const { start, end } = params;
            const width = end - start;
            const division = width / 4;

            addToLoadLocations(baseLine, start + division, sX, sY, eX, eY, cX, cY, lightLocs);
            addToLoadLocations(baseLine, end - division, sX, sY, eX, eY, cX, cY, lightLocs);

            const [dsX, dsY] = polyLine2EdgeEval(baseLine, 0, start, false);
            const [deX, deY] = polyLine2EdgeEval(baseLine, 0, end, false);
            const doorEdge = polyLine2ByTwoPoints(dsX, dsY, deX, deY);

            const [fX, fY] = polyLine2EdgeEvalOffset(doorEdge, 0, -60, 0.5, true);
            const [nx, ny] = polyLine2EdgeNormal(doorEdge, 0);

            openerLocs.push({ x: fX, y: fY, nx, ny });
          }
        }
      }
    }

    if (lightLocs.length === 0) {
      return [];
    }

    const lightsBB = aabb2ByPoints(lightLocs.map((x) => [x.x, x.y]));
    const lightCenter = aabb2Center(lightsBB);
    const candidate = await controllerSelectionFunc(controllerPlacementType, lightCenter);
    const controllerPosition = commitControllerSelection(
      candidate,
      ElectricalLayoutType.GARAGE_LIGHTS,
      ControllerType.SWITCH,
      lightCenter,
    );

    if (!controllerPosition) {
      return [];
    }

    const {
      position: [contX, contY],
    } = controllerPosition;

    const loads: Load[] = [];
    for (const loc of lightLocs.sort((a, b) => vec2Dist(a.x, a.y, contX, contY) - vec2Dist(b.x, b.y, contX, contY))) {
      loads.push({
        loadType: resolvedLoadType,
        loadDescriptor,
        position: [loc.x, loc.y],
        normal: [loc.nx, loc.ny],
        levelId: sourceRoom.level.id,
        sourceRoomHandle: sourceRoom.roomHandle,
      });
    }

    const lightElectricalGroup: GeneratedElectricalGroup = {
      levelId: opts.room.level.id,
      loads,
      controllers: [
        {
          type: ControllerType.SWITCH,
          switchType: ControllerSwitchType.STANDARD,
          position: controllerPosition,
          loadIndex: 0,
          levelId: sourceRoom.level.id,
          sourceRoomHandle: sourceRoom.roomHandle,
          candidateId: candidate?.candidate?.id,
          labels: makeBuiltInLabelTable("addToElectricalPlan"),
        },
      ],
    };

    if (!addOpener) {
      return [lightElectricalGroup];
    }

    const openerCandidate = candidate;
    if (!openerCandidate) {
      console.warn("no opening controller assigned");
      return [];
    }

    const openerGroups: GeneratedElectricalGroup[] = [];
    const openerLoadType = loadTypeFromSlots(LoadTypeSlot.GARAGE_DOOR_OPENER, elecSlots);
    const outletLoadType = loadTypeFromSlots(LoadTypeSlot.OUTLET_GFCI, elecSlots);

    for (const opener of openerLocs) {
      const { x: oX, y: oY, nx, ny } = opener;
      const openingControllerPosition = commitControllerSelection(
        openerCandidate,
        ElectricalLayoutType.GARAGE_LIGHTS,
        ControllerType.OPENER,
        [oX, oY],
      );

      const openerElectricalGroup: GeneratedElectricalGroup = {
        levelId: opts.room.level.id,
        loads: [
          {
            loadType: openerLoadType,
            loadDescriptor: loadCatalog[openerLoadType],
            position: [oX, oY],
            normal: [nx, ny],
            levelId: sourceRoom.level.id,
            sourceRoomHandle: sourceRoom.roomHandle,
          },
          {
            loadType: outletLoadType,
            loadDescriptor: loadCatalog[outletLoadType],
            position: [oX + nx * 12, oY + ny * 12],
            normal: [nx, ny],
            levelId: sourceRoom.level.id,
            sourceRoomHandle: sourceRoom.roomHandle,
          },
        ],
        controllers: openingControllerPosition
          ? [
              {
                type: ControllerType.OPENER,
                position: openingControllerPosition,
                loadIndex: 0,
                levelId: sourceRoom.level.id,
                sourceRoomHandle: sourceRoom.roomHandle,
                candidateId: openerCandidate?.candidate?.id,
                labels: makeBuiltInLabelTable("addToElectricalPlan"),
              },
            ]
          : [],
      };
      openerGroups.push(openerElectricalGroup);
    }

    return [lightElectricalGroup, ...openerGroups];
  };
}

function addToLoadLocations(
  baseLine: PolyLine2,
  center: number,
  sX: number,
  sY: number,
  eX: number,
  eY: number,
  cX: number,
  cY: number,
  lightLocations: LoadLocation[],
) {
  const [pX, pY] = polyLine2EdgeEval(baseLine, 0, center);
  const dist = line2PerpDistance(sX, sY, eX, eY, cX, cY);
  const [nx, ny] = polyLine2EdgeNormal(baseLine, 0, true);
  const [nsX, nsY] = vec2Scale(nx, ny, dist);
  lightLocations.push({ x: pX + nsX, y: pY + nsY, nx, ny });
}
